import dayjs from 'dayjs';

import { Intl } from '@src/_metronic/i18n/I18nProvider';

import store from '@app/store/store';

const getUserData = () => store.getState().auth.userData;

export const GDPR_NAME = 'EFF';

export const GDPR_FIELDS = [
    {
        slug: 'gdprEmail',
        map: 'email',
        label: 'TRANSLATOR.CONSENT_EMAIL',
    },
    {
        slug: 'gdprPixel',
        map: 'pixel',
        label: 'TRANSLATOR.CONSENT_PIXEL',
    },
    {
        slug: 'gdprPhone',
        map: 'phone',
        label: 'TRANSLATOR.CONSENT_PHONE',
    },
    {
        slug: 'gdprSms',
        map: 'sms',
        label: 'TRANSLATOR.CONSENT_SMS',
    },
    {
        slug: 'gdprPost',
        map: 'post',
        label: 'TRANSLATOR.CONSENT_POST',
    },
];

export const GDPR_GROUPS = {
    gdprAutosphere: 'AUTOSPHERE',
    gdprDealerships: 'DEALERSHIPS',
    gdprSecurycar: 'SECURYCAR',
};

/**
 * Get customer GDPR
 *
 * @param name
 * @param gdpr
 * @returns {{refDealership: *, name, gdprEmail: boolean | null, gdprEmailValidityDate: *, gdprPixel: boolean | null, gdprPixelValidityDate: *, gdprPhone: boolean | null, gdprPhoneValidityDate: *, gdprSms: boolean | null, gdprSmsValidityDate: *, gdprPost: boolean | null, gdprPostValidityDate: *}}
 */
export const getCustomerGdpr = (name, gdpr) => {
    const expectedValues = ['true', 'false', true, false];
    return {
        name,
        gdprEmail: expectedValues.includes(gdpr?.gdprEmail) ? String(gdpr?.gdprEmail) === 'true' : null,
        gdprEmailValidityDate: gdpr?.gdprEmailValidityDate,
        gdprPixel: expectedValues.includes(gdpr?.gdprPixel) ? String(gdpr?.gdprPixel) === 'true' : null,
        gdprPixelValidityDate: gdpr?.gdprPixelValidityDate,
        gdprPhone: expectedValues.includes(gdpr?.gdprPhone) ? String(gdpr?.gdprPhone) === 'true' : null,
        gdprPhoneValidityDate: gdpr?.gdprPhoneValidityDate,
        gdprSms: expectedValues.includes(gdpr?.gdprSms) ? String(gdpr?.gdprSms) === 'true' : null,
        gdprSmsValidityDate: gdpr?.gdprSmsValidityDate,
        gdprPost:
            expectedValues.includes(gdpr?.gdprPost) || expectedValues.includes(gdpr?.gdprAddress)
                ? String(gdpr?.gdprPost) === 'true' || String(gdpr?.gdprAddress) === 'true'
                : null,
        gdprPostValidityDate: gdpr?.gdprPostValidityDate,
        ...(gdpr?.refDealershipId ? { refDealership: gdpr?.refDealershipId } : {}),
        ...(gdpr?.refDealership ? { refDealership: gdpr.refDealership } : {}),
    };
};

/**
 * Get customer GDPRS
 *
 * @param customer
 * @param withDisplay
 * @returns {unknown[]}
 */
export const getCustomerGdprs = (customer, withDisplay = false) => {
    const userData = getUserData();
    let gdpr = {};

    // Add VCU consents
    Object.keys(GDPR_GROUPS).forEach((group) => {
        const customerGroups = customer[group];

        if (customerGroups) {
            if (group === 'gdprDealerships') {
                if (Array.isArray(customerGroups)) {
                    customerGroups.forEach((customerGroup) => {
                        gdpr[customerGroup.refDealershipName] = getCustomerGdpr(
                            customerGroup.refDealershipName,
                            customerGroup,
                        );
                    });
                }
            } else {
                const name = GDPR_GROUPS[group];
                gdpr[name] = getCustomerGdpr(name, customerGroups);
            }
        }
    });

    // Add customer consents
    if (customer?.consents) {
        customer.consents.forEach((consent) => {
            gdpr[consent.name] = getCustomerGdpr(consent.name, consent);
        });
    }

    // Add created / updated consents
    if (customer?.new_consents) {
        Object.entries(customer.new_consents).forEach(([consent, value]) => {
            const splittedConsent = consent.split('_');
            const dealershipId = splittedConsent[0];
            const dealership = Object.entries(userData.dealerships).find(([code]) => code === dealershipId);
            if (dealership === undefined) {
                return;
            }

            const name = dealership[1]?.title;
            const type = splittedConsent[splittedConsent.length - 1];

            const consentKey =
                Object.keys(gdpr).find((key) => gdpr[key]?.refDealership === dealershipId) ?? dealershipId;

            gdpr[consentKey] = getCustomerGdpr(name, {
                ...gdpr[consentKey],
                [type]: value,
                refDealership: dealershipId,
            });
        });
    }

    // Object to array
    gdpr = Object.values(gdpr);

    if (withDisplay) {
        const dealershipConsents = [];
        gdpr.slice()
            .reverse()
            .forEach((consent, index, object) => {
                if (consent?.refDealership) {
                    dealershipConsents.unshift(consent);
                    gdpr.splice(object.length - 1 - index, 1);
                }
            });

        dealershipConsents.sort((a, b) => (a.name > b.name ? 1 : -1));
        gdpr.push(dealershipConsents);
    }

    // Set Securycar first
    const gdprSecurycarIdx = gdpr.findIndex((element) => element.name === GDPR_GROUPS.gdprSecurycar);
    if (gdprSecurycarIdx !== -1) {
        const gdprSecurycar = gdpr[gdprSecurycarIdx];
        gdpr.splice(gdprSecurycarIdx, 1);
        gdpr.unshift(gdprSecurycar);
    }

    // Set Autosphere first (before Securycar)
    const gdprAutosphereIdx = gdpr.findIndex((element) => element.name === GDPR_GROUPS.gdprAutosphere);
    if (gdprAutosphereIdx !== -1) {
        const gdprAutosphere = gdpr[gdprAutosphereIdx];
        gdpr.splice(gdprAutosphereIdx, 1);
        gdpr.unshift(gdprAutosphere);
    }

    return gdpr;
};

/**
 * Map customer gdpr
 *
 * @param gdprs
 */
export const mapCustomerGdpr = (gdprs) => {
    gdprs.forEach((gdpr) => {
        GDPR_FIELDS.forEach((field) => {
            gdpr[field.map] = gdpr[field.slug];
            delete gdpr[field.slug];
        });
    });

    return gdprs;
};

/**
 * Keep only the consent the user is able to edit
 *
 * @param customer
 * @param gdprs
 */
export const getEditableGdprs = (customer, gdprs) => {
    const editableDealership = customer?.consentDealership ?? customer?.dealership;

    if (!editableDealership) {
        return [];
    }

    return gdprs.filter((gdpr) => gdpr?.refDealership === editableDealership);
};

/**
 * Map customer for the contact API payload
 *
 * @param customer
 * @param {boolean} update - update (true) or creation (false)
 */
export const mapVcuCustomer = (customer, update = false) => {
    if (!customer) return {};

    const gdprs = [...(mapCustomerGdpr(getCustomerGdprs(customer)) ?? []), ...(customer?.gdpr ?? [])];

    return {
        additionalAddress: customer?.additionalAddress || '',
        address: customer?.address || '',
        autosphereId: customer?.autosphereId ? parseInt(customer.autosphereId, 10) : null,
        birthday:
            customer?.birthday && dayjs(customer.birthday).isValid()
                ? dayjs(customer.birthday).format('YYYY-MM-DD')
                : '',
        city: customer?.city || '',
        civility: customer?.civility ? customer.civility.toLowerCase() : '',
        country: customer?.country || '',
        dealershipKnow: customer?.dealershipKnow || null,
        deliveryId: customer?.deliveryId ? parseInt(customer.deliveryId, 10) : null,
        email: customer?.email || '',
        firstname: customer?.firstname || '',
        isClient: customer?.isClient || false,
        lastname: customer?.lastname || '',
        mobile: customer?.mobile || '',
        phone: customer?.phone || '',
        vcuId: customer?.vcuId || null,
        vehicleKnow: customer?.vehicleKnow || [],
        zipcode: customer?.zipcode || '',
        gdpr: update ? getEditableGdprs(customer, gdprs) : gdprs,
        md5: customer?.md5 || null,
        isPro: customer?.isPro ?? null,
        siren: customer?.siren || null,
        siret: customer?.siret || null,
        ape: customer?.ape ? { code: customer?.ape?.code, value: customer?.ape?.value } : null,
        companyName: customer?.companyName || null,
        companyLegalStatus: customer?.companyLegalStatus || null,
        companyPhone: customer?.companyPhone || null,
        proPhone: customer?.proPhone || null,
        function: customer?.function || null,
        role: customer?.role || null,
        baseDmsIdentifier: customer?.baseDmsIdentifier ?? undefined,
    };
};

export const withMainAddressingMesh = (addressingMeshes) => {
    const mainCode = addressingMeshes.find(({ isMain }) => isMain)?.code ?? addressingMeshes[0]?.code;
    return addressingMeshes.map((addressingMesh) => ({ ...addressingMesh, isMain: addressingMesh.code === mainCode }));
};

/**
 * Get customer for DMS
 *
 * @param {Object} formCustomer - The customer form data
 * @param {boolean} update - update (true) or creation (false)
 * @returns {Object} - The mapped customer object for DMS
 */
export const mapCustomerDms = (formCustomer, update) => {
    if (!formCustomer) return {};

    if (formCustomer.siret) {
        formCustomer.siret = formCustomer.siret.replace(/\s+/g, '');

        // Only recalculate SIREN if we don't already have one
        const isBelgianSiren = getUserData()?.context === 'efb';
        const maxDigits = isBelgianSiren ? 10 : 9;
        if (!formCustomer.siren && formCustomer.siret.length >= maxDigits) {
            formCustomer.siren = formCustomer.siret.substring(0, maxDigits);
        }
    }
    if (formCustomer.vat) {
        formCustomer.vat = formCustomer.vat.replace(/\s+/g, '').toUpperCase();
    }

    return {
        ...mapVcuCustomer(formCustomer, update),
        companyLegalCode: formCustomer?.companyLegalCode,
        companyLegalStatus: formCustomer?.companyLegalStatus,
        vat: formCustomer?.vat,
        source: formCustomer?.source ?? undefined,
        dmsId: update ? undefined : formCustomer?.contactDms?.[0]?.dmsId,
        dmsDealership: formCustomer?.dealership,
        addressingMeshes: formCustomer?.addressingMeshes ?? undefined,
    };
};

/**
 * Get customer civility
 *
 * @param civility
 * @returns {string|null}
 */
export const getCivility = (civility) => {
    if (civility) {
        switch (civility.toLowerCase()) {
            case 'mr':
            case 'm':
                return 'M.';
            case 'mme':
                return 'Mme.';
            default:
                return '';
        }
    }
    return null;
};

export const mapCustomerFieldSelection = (new_customer, existing_customer) => {
    const fields = [
        {
            label: Intl.formatMessage({ id: 'CUSTOMERS.MODAL.CREATE.CIVILITY' }),
            field: 'civility',
        },
        {
            label: Intl.formatMessage({ id: 'CUSTOMERS.MODAL.CREATE.FIRSTNAME' }),
            field: 'firstname',
        },
        {
            label: Intl.formatMessage({ id: 'CUSTOMERS.MODAL.CREATE.LASTNAME' }),
            field: 'lastname',
        },
        {
            label: Intl.formatMessage({ id: 'CUSTOMERS.MODAL.CREATE.PHONE' }),
            field: 'phone',
        },
        {
            label: Intl.formatMessage({ id: 'CUSTOMERS.MODAL.CREATE.MOBILE' }),
            field: 'mobile',
        },
        {
            label: Intl.formatMessage({ id: 'CUSTOMERS.MODAL.CREATE.EMAIL' }),
            field: 'email',
        },
    ];

    return fields?.reduce((acc, field) => {
        if (new_customer?.[field?.field]?.toLowerCase?.() !== existing_customer?.[field?.field]?.toLowerCase?.()) {
            acc.push({
                ...field,
                old_value: new_customer?.[field?.field] || '',
                new_value: existing_customer?.[field?.field] || '',
            });
        }

        return acc;
    }, []);
};

/**
 * Check if the client has any consent, even a pending one
 *
 * @param client
 */
export const hasConsents = (client) => Array.isArray(client?.consents) && client.consents.length > 0;

/**
 * Check if any Gdrp is set to true
 */
export const getCustomerAgeRange = () => {
    const year = new Date().getFullYear();
    const startYear = year - 100;
    const endYear = year - 18;
    return { startYear, endYear };
};

/**
 * Check if vehicle is in dealership
 *
 * @param apvRequest - The request object to check for vehicle presence
 * @returns {boolean} - True if the vehicle is present, false otherwise
 */
export const getVehiclePresence = (apvRequest) => !!apvRequest;

export const getDealershipsWithBaseDms = () => {
    const userData = getUserData();
    const userDealerships = userData?.dealerships ?? {};

    return (
        Object.values(userDealerships)
            .filter((d) => !!d?.baseDmsIdentifier)
            // TODO remove this DSC filter when they can manage private customer
            .filter((dmsFilter) => dmsFilter?.baseDmsName !== 'DCS')
    );
};

export const getUniqueBaseDmsIds = () => {
    const dealershipsWithBase = getDealershipsWithBaseDms();
    return [...new Set(dealershipsWithBase.map((d) => d?.baseDmsIdentifier))];
};

export const getBaseDmsCounts = (contactDms) => {
    const uniqueBaseIds = getUniqueBaseDmsIds();

    const countWithDmsId = uniqueBaseIds.filter((baseId) => {
        const contact = contactDms?.find((c) => c.baseDmsIdentifier === baseId);
        return contact && contact.dmsId !== null;
    }).length;

    const countWithoutDmsId = uniqueBaseIds.filter((baseId) => {
        const contact = contactDms?.find((c) => c.baseDmsIdentifier === baseId);
        return !contact || contact.dmsId === null;
    }).length;

    return {
        countWithDmsId,
        countWithoutDmsId,
        total: uniqueBaseIds.length,
    };
};

export const getBaseDmsName = (contactDmsId) => {
    const dealershipsWithBase = getDealershipsWithBaseDms();
    return dealershipsWithBase.find((d) => d.baseDmsIdentifier === contactDmsId)?.baseDmsName;
};

export const getBaseDmsNameList = () => {
    const dealershipsWithBase = getDealershipsWithBaseDms();

    return [...new Set(dealershipsWithBase.map((base) => base.baseDmsName))].join(' / ');
};

export const getDmsList = (contactDms) => {
    const dealershipsWithBase = getDealershipsWithBaseDms();
    const uniqueBaseIds = getUniqueBaseDmsIds();

    const dmsList = uniqueBaseIds.map((baseId) => {
        const dealershipsForBase = dealershipsWithBase.filter((d) => d.baseDmsIdentifier === baseId);
        const contact = contactDms?.find((c) => c.baseDmsIdentifier === baseId);
        const dmsId = contact?.dmsId;
        const brands = [...new Set(dealershipsForBase.map((d) => d.brand))];

        const groupsDms = brands.map((brand) => {
            const cities = dealershipsForBase.filter((d) => d.brand === brand).map((d) => d.city);
            return { brand, cities };
        });

        return {
            baseDmsIdentifier: baseId,
            baseDmsName: dealershipsForBase[0]?.baseDmsName ?? '-',
            groupsDms,
            dmsId,
        };
    });

    return dmsList.sort((a, b) => {
        if (a.dmsId && !b.dmsId) return -1;
        if (!a.dmsId && b.dmsId) return 1;
        return 0;
    });
};

export const formatBrandsAndCities = (groupsDms) => {
    if (!groupsDms) return [];

    return groupsDms.map(({ brand, cities }) =>
        cities.length === 1 ? `${brand} ${cities[0]}` : `${brand} : ${cities.join(', ')}`,
    );
};

/**
 * Get customer type label based on customer properties
 *
 * @param customer - Customer object containing isPro and isClient properties
 * @returns {string} - Customer type label or empty string
 */
export const getClientTypeLabel = (customer) => {
    if (customer && !customer.isPro) {
        const typeId = customer.isClient ? 'CLIENT' : 'PROSPECT';
        return Intl.formatMessage({ id: `TRANSLATOR.${typeId}` }).toLowerCase();
    }
    return '';
};
